import React, { useContext, useState } from 'react';
import { Button, Checkbox, Col, Form, Row, Spin, message } from 'antd';
import PropTypes from 'prop-types';
import FileSelect from '../../../../../../components/form/upload/SingleUpload';
import Paragraph from '../../../../../../components/common/Paragraph';
import tokens from '../../../../../../utils/tokens';
import legalBackgroundItems from './legalBackgroundItems';
import file from '../../../../../../utils/file';
import LegalEnrollmentApi from '../../../../api-enrollmentLegal';
import { EnrollmentContext } from '../../../../../../providers/EnrollmentContextProvider';
import { EnrollmentFormContext } from '../../../../../../providers/EnrollmentFormContextProvider';
import EnrollmentDocumentsApi from '../../../../../enrollment-documents/apis/EnrollmentDocumentsApi';

const LegalBackgroundForm = ({ onFinish }) => {
  const [isLoading, setLoading] = useState(false);
  const { enrollmentId } = useContext(EnrollmentContext);
  const { enrollmentForm } = useContext(EnrollmentFormContext);
  const [documentsGroups, setDocumentsGroups] = useState(
    enrollmentForm.getFieldValue('documents') || {}
  );

  const handleDoesApplyCheck = () => {
    enrollmentForm.validateFields();
  };

  const getSubmitHandler = (shouldCallOnFinish) => async () => {
    const values = enrollmentForm.getFieldsValue();
    setLoading(true);
    const documentsGroupsToUpload = [];

    await Promise.all(
      Object.keys(values.documents).map(async (documentTypeKey) => {
        if (values.documents[documentTypeKey].doesNotApply) {
          return;
        }
        const documents = [];
        await Promise.all(
          (values.documents[documentTypeKey].files || []).map(async (uploadedFile) => {
            const { name, originFileObj, url } = uploadedFile;

            if (url) {
              return;
            }

            const base64 = await file.toBase64(originFileObj);

            documents.push({
              filename: name,
              base64,
              typeKey: documentTypeKey,
            });
          })
        );
        documentsGroupsToUpload.push({
          doesApply: !values.documents[documentTypeKey].doesNotApply,
          typeKey: documentTypeKey,
          documents,
        });
      })
    );

    const requestPayload = {
      documentsGroups: documentsGroupsToUpload,
    };

    try {
      const response = await LegalEnrollmentApi.updateLegalEnrollment(enrollmentId, requestPayload);

      const updatedDocumentsGroups = {};
      response.data.documentsGroups.forEach((documentsGroup) => {
        updatedDocumentsGroups[documentsGroup.type.key] = {
          files: documentsGroup.documents.map((document) => ({
            uid: document.id,
            name: document.name,
            status: 'done',
            url: document.url,
          })),
        };
      });

      setDocumentsGroups(updatedDocumentsGroups);

      enrollmentForm.setFieldsValue({
        documents: updatedDocumentsGroups,
      });

      if (shouldCallOnFinish) onFinish();
    } catch (error) {
      message.error('Error al subir los documentos, intente nuevamente');
    }

    setLoading(false);
  };

  const getFileRemoveHandler = (itemKey) => async (removedFile) => {
    setLoading(true);

    try {
      await EnrollmentDocumentsApi.deleteDocument(enrollmentId, removedFile.uid);

      const updatedDocumentsGroups = {
        ...documentsGroups,
        [itemKey]: {
          ...(documentsGroups[itemKey] || {}),
          files: (documentsGroups[itemKey].files || []).filter(
            (theFile) => theFile.uid !== removedFile.uid
          ),
        },
      };

      setDocumentsGroups(updatedDocumentsGroups);

      enrollmentForm.setFieldsValue({
        documents: updatedDocumentsGroups,
      });
    } catch (e) {
      message.error('No se pudo eliminar el documento');
    }

    setLoading(false);
  };

  return (
    <Spin spinning={isLoading}>
      <Form form={enrollmentForm} layout="vertical" onFinish={getSubmitHandler(true)}>
        <Paragraph noBottomMargin strong>
          Antecedentes Legales Requeridos
        </Paragraph>
        <Paragraph>Archivos JPG, PNG o PDF - Tamaño máximo 10MB.</Paragraph>
        {legalBackgroundItems.map((item, index) => (
          <Row gutter={[tokens.forms.horizontalGutterInPx]} key={item.key}>
            <Col span={24}>
              <Paragraph>
                <b>
                  {index + 1}. {item.label}
                </b>{' '}
                {item.asideLabel}
              </Paragraph>
            </Col>
            <Col lg={14} xs={24}>
              <Form.Item
                name={['documents', item.key, 'files']}
                rules={[
                  {
                    validator: (_, fileList) => {
                      if (
                        !enrollmentForm.getFieldValue(['documents', item.key, 'doesNotApply']) &&
                        !fileList?.length > 0
                      ) {
                        return Promise.reject(new Error('Documento obligatorio'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                shouldUpdate
              >
                <FileSelect
                  accept={'image/*,.pdf'}
                  maxSizeKb={10240}
                  maxFileCount={5}
                  fileList={documentsGroups[item.key]?.files}
                  onRemove={getFileRemoveHandler(item.key)}
                >
                  Cargar documento
                </FileSelect>
              </Form.Item>
            </Col>
            <Col lg={10} xs={24}>
              <Form.Item
                name={['documents', item.key, 'doesNotApply']}
                valuePropName="checked"
                shouldUpdate
              >
                <Checkbox style={{ marginTop: '10px' }} onChange={handleDoesApplyCheck}>
                  {' '}
                  No aplica
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        ))}

        <Row gutter={[30]}>
          <Col lg={12} xs={24}>
            <Button block type="primary" htmlType="submit">
              Continuar
            </Button>
          </Col>
          <Col lg={12} xs={24}>
            <Button block type="primary" ghost onClick={getSubmitHandler()}>
              Guardar
            </Button>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

LegalBackgroundForm.propTypes = {
  onFinish: PropTypes.func.isRequired,
};

export default LegalBackgroundForm;
